import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, LoadingSpinner } from '../../components/atoms';
import { FormField } from '../../components/molecues/FormField';
import { DurationHmsField } from '../../components/molecues/DurationHmsField';
import { useSeriesActions } from '../../hooks/useSeries';
import { getLanguageService, getGenreService } from '../../service/ServiceRegistry';
import type { Language } from '../../types/api/language';
import type { Genre } from '../../types/api/genre';
import type { CreateDramaRequest } from '../../types/api/drama';
import toast from 'react-hot-toast';

/**
 * SeriesCreatePage Component
 *
 * Separate page for creating series with optional thumbnail upload.
 * Features:
 * - Form with all series fields
 * - Thumbnail file upload
 * - Language dropdown selection
 * - Genre multi-select with primary genre
 * - Validation & error handling
 */
type SeriesCreateForm = Omit<CreateDramaRequest, 'dramaType'>;

export const SeriesCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { createSeriesWithThumbnail } = useSeriesActions();
  const languageService = getLanguageService();
  const genreService = getGenreService();

  const [formData, setFormData] = useState<SeriesCreateForm>({
    title: '',
    description: '',
    languageId: '',
    totalSeasons: 1,
    totalEpisodes: 1,
    episodeDuration: 2700,
    initialGenreIds: [],
    primaryGenreId: '',
    thumbnailUrl: '',
  });

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Options for dropdowns
  const [languages, setLanguages] = useState<Language[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Load languages and genres
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setLoadingOptions(true);
        const [langsResponse, genresResponse] = await Promise.all([
          languageService.getLanguages({ page: 1, size: 100 }),
          genreService.getPublicGenres({ page: 1, size: 200 }),
        ]);
        setLanguages(langsResponse.items);
        setGenres(genresResponse.items);
      } catch (error) {
        console.error('Failed to load options:', error);
        toast.error('Failed to load languages and genres');
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [languageService, genreService]);

  // Handle thumbnail file selection
  const handleThumbnailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors((prev) => ({ ...prev, thumbnail: 'Please select an image file' }));
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, thumbnail: 'File size must be less than 10MB' }));
        return;
      }
      setThumbnailFile(file);
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.thumbnail;
        return newErrors;
      });

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleChange = (field: keyof SeriesCreateForm, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleGenreToggle = (genreId: string) => {
    setFormData((prev) => {
      const currentIds = prev.initialGenreIds || [];
      const newIds = currentIds.includes(genreId)
        ? currentIds.filter((id) => id !== genreId)
        : [...currentIds, genreId];

      // If primary genre is removed, clear it
      const newPrimaryId = prev.primaryGenreId === genreId ? '' : prev.primaryGenreId;

      return {
        ...prev,
        initialGenreIds: newIds,
        primaryGenreId: newPrimaryId,
      };
    });
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.languageId) {
      newErrors.languageId = 'Language is required';
    }
    if (!formData.totalSeasons || formData.totalSeasons < 1) {
      newErrors.totalSeasons = 'Total seasons must be at least 1';
    }
    if (!formData.totalEpisodes || formData.totalEpisodes < 1) {
      newErrors.totalEpisodes = 'Total episodes must be at least 1';
    }
    if (!formData.episodeDuration || formData.episodeDuration < 1) {
      newErrors.episodeDuration = 'Episode duration must be at least 1 second';
    }
    if (!formData.initialGenreIds || formData.initialGenreIds.length === 0) {
      newErrors.initialGenreIds = 'At least one genre is required';
    }
    if (!formData.primaryGenreId) {
      newErrors.primaryGenreId = 'Primary genre is required';
    }
    if (!thumbnailFile) {
      newErrors.thumbnail = 'Thumbnail image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const { thumbnailUrl: _thumbnailUrl, ...rest } = formData;
      await createSeriesWithThumbnail(rest, thumbnailFile!);
      toast.success('Series created successfully');
      navigate('/pages/series');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create series');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Create Series</h1>
          <p className="text-gray-600 mt-1">Add a new series to the platform</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/pages/series')}>
          Cancel
        </Button>
      </div>

      {/* Form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6"
      >
        {/* Basic Information */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 text-left">
            Basic Information
          </h2>

          <FormField
            label="Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={errors.title}
            required
            placeholder="Enter series title"
          />

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Enter series description"
              rows={4}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.description && (
              <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
                Language <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.languageId}
                onChange={(e) => handleChange('languageId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              >
                <option value="">Select a language</option>
                {languages
                  .filter((lang) => lang.isActive)
                  .map((lang) => (
                    <option key={lang.languageId} value={lang.languageId}>
                      {lang.name} ({lang.nameMm})
                    </option>
                  ))}
              </select>
              {errors.languageId && (
                <p className="text-sm text-red-600 mt-1.5">{errors.languageId}</p>
              )}
            </div>

            <DurationHmsField
              label="Episode duration"
              valueSeconds={formData.episodeDuration}
              onSecondsChange={(sec) => handleChange('episodeDuration', sec ?? 0)}
              required
              error={errors.episodeDuration}
              helperText="Stored and sent as total seconds per episode."
              idPrefix="series-create-episode-duration"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              label="Total Seasons"
              type="number"
              value={formData.totalSeasons.toString()}
              onChange={(e) =>
                handleChange('totalSeasons', parseInt(e.target.value) || 0)
              }
              error={errors.totalSeasons}
              required
              min={1}
              placeholder="1"
            />

            <FormField
              label="Total Episodes"
              type="number"
              value={formData.totalEpisodes.toString()}
              onChange={(e) =>
                handleChange('totalEpisodes', parseInt(e.target.value) || 0)
              }
              error={errors.totalEpisodes}
              required
              min={1}
              placeholder="16"
            />
          </div>
        </div>

        {/* Thumbnail Upload */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 text-left">
            Thumbnail
          </h2>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Thumbnail Image <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.thumbnail && (
              <p className="text-sm text-red-600 mt-1.5">{errors.thumbnail}</p>
            )}
            <p className="text-sm text-gray-500 mt-1.5">
              JPEG, PNG, or WebP. Max 10MB. Will be auto-uploaded to storage.
            </p>

            {thumbnailPreview && (
              <div className="mt-4">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-48 h-64 object-cover rounded-lg border border-gray-300"
                />
              </div>
            )}
          </div>
        </div>

        {/* Genres */}
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-gray-900 border-b border-gray-200 pb-2 text-left">
            Genres
          </h2>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Select Genres <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {genres
                .filter((genre) => genre.isActive)
                .map((genre) => (
                  <label
                    key={genre.genreId}
                    className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.initialGenreIds?.includes(genre.genreId)}
                      onChange={() => handleGenreToggle(genre.genreId)}
                      className="w-4 h-4 text-[#EAB308] border-gray-300 rounded focus:ring-[#EAB308]"
                    />
                    <span className="text-sm text-gray-700">{genre.name}</span>
                  </label>
                ))}
            </div>
            {errors.initialGenreIds && (
              <p className="text-sm text-red-600 mt-1.5">{errors.initialGenreIds}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Primary Genre <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.primaryGenreId}
              onChange={(e) => handleChange('primaryGenreId', e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            >
              <option value="">Select primary genre</option>
              {genres
                .filter(
                  (genre) =>
                    genre.isActive && formData.initialGenreIds?.includes(genre.genreId)
                )
                .map((genre) => (
                  <option key={genre.genreId} value={genre.genreId}>
                    {genre.name}
                  </option>
                ))}
            </select>
            {errors.primaryGenreId && (
              <p className="text-sm text-red-600 mt-1.5">{errors.primaryGenreId}</p>
            )}
            {formData.initialGenreIds && formData.initialGenreIds.length > 0 && (
              <p className="text-sm text-gray-500 mt-1.5 text-left">
                Select from the genres you&apos;ve chosen above.
              </p>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/pages/series')}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>
            Create Series
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SeriesCreatePage;


